import React, { useState, useCallback, useEffect } from "react";
import styles from "./PolicyEditor.module.css";
import { Button, ButtonGroup } from "aible";
import { ClawTaskItem } from "components/Templates/AgentC";
import ProviderProfileEditor, { EndpointProfileEditor } from "./ProviderProfileEditor";
import { Toggle } from "./OpenShellCreate";
import { InfoTip } from "./InfoTip";
import { ProviderCreator, ProviderEditor } from "./ProviderEditor";
export { InfoTip } from "./InfoTip";
// ── Default policy ─────────────────────────────────────────────────────
export const DEFAULT_POLICY = {
    version: 1,
    filesystem_policy: {
        include_workdir: true,
        read_only: ["/usr", "/lib", "/proc", "/dev/urandom", "/app", "/etc", "/var/log"],
        read_write: ["/sandbox", "/tmp", "/dev/null", "/home/linuxbrew"]
    },
    landlock: {
        compatibility: "best_effort"
    },
    process: {
        run_as_user: "sandbox",
        run_as_group: "sandbox"
    },
    network_policies: {
        aible_mcp: {
            name: "aible_mcp",
            endpoints: [
                {
                    host: "mcp-server.aible.svc.cluster.local",
                    port: 8000
                }
            ],
            binaries: [
                {
                    path: "/usr/bin/curl"
                },
                {
                    path: "/usr/bin/node"
                },
                {
                    path: "/usr/local/bin/node"
                }
            ]
        },
        aiq_research: {
            name: "aiq_research",
            endpoints: [
                {
                    host: "34.60.46.14",
                    port: 8005
                }
            ],
            binaries: [
                {
                    path: "/sandbox/.uv/python/cpython-3.13.12-linux-aarch64-gnu/bin/python3.13"
                },
                {
                    path: "/sandbox/.uv/python/cpython-3.14.3-linux-aarch64-gnu/bin/python3.14"
                },
                {
                    path: "/usr/bin/python3.13"
                }
            ]
        },
        allow_host_docker_internal_8002: {
            name: "allow_host_docker_internal_8002",
            endpoints: [
                {
                    host: "host.docker.internal",
                    port: 8002,
                    allowed_ips: ["172.17.0.1"]
                }
            ],
            binaries: [
                {
                    path: "/usr/bin/curl"
                },
                {
                    path: "/usr/bin/node"
                },
                {
                    path: "/usr/local/bin/node"
                }
            ]
        },
        anthropic: {
            name: "anthropic",
            endpoints: [
                {
                    host: "api.anthropic.com",
                    port: 443
                }
            ],
            binaries: [
                {
                    path: "/usr/bin/node"
                },
                {
                    path: "/usr/local/bin/node"
                }
            ]
        },
        brave: {
            name: "brave",
            endpoints: [
                {
                    host: "api.search.brave.com",
                    port: 443
                }
            ],
            binaries: [
                {
                    path: "/usr/bin/*"
                }
            ]
        },
        claude_code: {
            name: "claude_code",
            endpoints: [
                {
                    host: "api.anthropic.com",
                    port: 443,
                    protocol: "rest",
                    tls: "terminate",
                    enforcement: "enforce",
                    rules: [
                        {
                            allow: {
                                method: "*",
                                path: "/**"
                            }
                        }
                    ]
                },
                {
                    host: "statsig.anthropic.com",
                    port: 443,
                    rules: [
                        {
                            allow: {
                                method: "*",
                                path: "/**"
                            }
                        }
                    ]
                },
                {
                    host: "sentry.io",
                    port: 443,
                    rules: [
                        {
                            allow: {
                                method: "*",
                                path: "/**"
                            }
                        }
                    ]
                }
            ],
            binaries: [
                {
                    path: "/usr/local/bin/claude"
                }
            ]
        },
        clawhub: {
            name: "clawhub",
            endpoints: [
                {
                    host: "clawhub.com",
                    port: 443,
                    protocol: "rest",
                    tls: "terminate",
                    enforcement: "enforce",
                    rules: [
                        {
                            allow: {
                                method: "GET",
                                path: "/**"
                            }
                        },
                        {
                            allow: {
                                method: "POST",
                                path: "/**"
                            }
                        }
                    ]
                }
            ],
            binaries: [
                {
                    path: "/usr/local/bin/openclaw"
                }
            ]
        },
        clawhub_ai: {
            name: "allow_clawhub_ai_443",
            endpoints: [
                {
                    host: "clawhub.ai",
                    port: 443
                }
            ],
            binaries: [
                {
                    path: "/usr/bin/node"
                },
                {
                    path: "/usr/local/bin/node"
                }
            ]
        },
        gemini: {
            name: "gemini",
            endpoints: [
                {
                    host: "generativelanguage.googleapis.com",
                    port: 443
                }
            ],
            binaries: [
                {
                    path: "/usr/bin/*"
                }
            ]
        },
        github: {
            name: "github",
            endpoints: [
                {
                    host: "github.com",
                    port: 443,
                    access: "full"
                },
                {
                    host: "api.github.com",
                    port: 443,
                    access: "full"
                }
            ],
            binaries: [
                {
                    path: "/usr/bin/gh"
                },
                {
                    path: "/usr/bin/git"
                }
            ]
        },
        npm_yarn: {
            name: "npm_yarn",
            endpoints: [
                {
                    host: "registry.npmjs.org",
                    port: 443,
                    access: "full",
                    tls: "skip"
                },
                {
                    host: "registry.yarnpkg.com",
                    port: 443,
                    access: "full",
                    tls: "skip"
                }
            ],
            binaries: [
                {
                    path: "/usr/local/bin/npm*"
                },
                {
                    path: "/usr/local/bin/npx*"
                },
                {
                    path: "/usr/local/bin/node*"
                },
                {
                    path: "/usr/local/bin/yarn*"
                },
                {
                    path: "/usr/bin/npm*"
                },
                {
                    path: "/usr/bin/node*"
                }
            ]
        },
        nvidia: {
            name: "nvidia",
            endpoints: [
                {
                    host: "integrate.api.nvidia.com",
                    port: 443,
                    protocol: "rest",
                    tls: "terminate",
                    enforcement: "enforce",
                    rules: [
                        {
                            allow: {
                                method: "*",
                                path: "/**"
                            }
                        }
                    ]
                },
                {
                    host: "inference-api.nvidia.com",
                    port: 443,
                    protocol: "rest",
                    tls: "terminate",
                    enforcement: "enforce",
                    rules: [
                        {
                            allow: {
                                method: "*",
                                path: "/**"
                            }
                        }
                    ]
                }
            ],
            binaries: [
                {
                    path: "/usr/local/bin/claude"
                },
                {
                    path: "/usr/local/bin/openclaw"
                }
            ]
        },
        openai: {
            name: "openai",
            endpoints: [
                {
                    host: "api.openai.com",
                    port: 443
                }
            ],
            binaries: [
                {
                    path: "/usr/bin/node"
                },
                {
                    path: "/usr/local/bin/node"
                }
            ]
        },
        openclaw_api: {
            name: "openclaw_api",
            endpoints: [
                {
                    host: "openclaw.ai",
                    port: 443,
                    protocol: "rest",
                    tls: "terminate",
                    enforcement: "enforce",
                    rules: [
                        {
                            allow: {
                                method: "GET",
                                path: "/**"
                            }
                        },
                        {
                            allow: {
                                method: "POST",
                                path: "/**"
                            }
                        }
                    ]
                }
            ],
            binaries: [
                {
                    path: "/usr/local/bin/openclaw"
                }
            ]
        },
        openclaw_docs: {
            name: "openclaw_docs",
            endpoints: [
                {
                    host: "docs.openclaw.ai",
                    port: 443,
                    protocol: "rest",
                    tls: "terminate",
                    enforcement: "enforce",
                    rules: [
                        {
                            allow: {
                                method: "GET",
                                path: "/**"
                            }
                        }
                    ]
                }
            ],
            binaries: [
                {
                    path: "/usr/local/bin/openclaw"
                }
            ]
        },
        slack: {
            name: "slack",
            endpoints: [
                {
                    host: "slack.com",
                    port: 443,
                    protocol: "rest",
                    tls: "terminate",
                    enforcement: "enforce",
                    rules: [
                        {
                            allow: {
                                method: "GET",
                                path: "/**"
                            }
                        },
                        {
                            allow: {
                                method: "POST",
                                path: "/**"
                            }
                        }
                    ]
                },
                {
                    host: "api.slack.com",
                    port: 443,
                    protocol: "rest",
                    tls: "terminate",
                    enforcement: "enforce",
                    rules: [
                        {
                            allow: {
                                method: "GET",
                                path: "/**"
                            }
                        },
                        {
                            allow: {
                                method: "POST",
                                path: "/**"
                            }
                        }
                    ]
                },
                {
                    host: "hooks.slack.com",
                    port: 443,
                    protocol: "rest",
                    tls: "terminate",
                    enforcement: "enforce",
                    rules: [
                        {
                            allow: {
                                method: "GET",
                                path: "/**"
                            }
                        },
                        {
                            allow: {
                                method: "POST",
                                path: "/**"
                            }
                        }
                    ]
                },
                {
                    host: "10.200.0.1",
                    port: 3128,
                    access: "full"
                },
                {
                    host: "wss-primary.slack.com",
                    port: 443,
                    access: "full"
                },
                {
                    host: "wss-backup.slack.com",
                    port: 443,
                    access: "full"
                }
            ],
            binaries: [
                {
                    path: "/usr/local/bin/node"
                },
                {
                    path: "/usr/bin/node"
                },
                {
                    path: "/usr/bin/openclaw"
                },
                {
                    path: "/usr/bin/npm"
                },
                {
                    path: "/usr/bin/curl"
                }
            ]
        },
        google_oauth2: {
            name: "google-oauth2-token",
            endpoints: [
                {
                    host: "oauth2.googleapis.com",
                    port: 443,
                    protocol: "rest",
                    request_body_credential_rewrite: true,
                    enforcement: "enforce",
                    rules: [
                        {
                            allow: {
                                method: "*",
                                path: "/**"
                            }
                        }
                    ]
                }
            ],
            binaries: [
                {
                    path: "/home/linuxbrew/.linuxbrew/Homebrew/Cellar/gogcli/0.21.0/bin/gog"
                }
            ]
        },
        brew: {
            name: "brew",
            endpoints: [
                {
                    host: "formulae.brew.sh",
                    port: 443,
                    access: "full",
                    tls: "skip"
                },
                {
                    host: "github.com",
                    port: 443,
                    access: "full",
                    tls: "skip"
                },
                {
                    host: "api.github.com",
                    port: 443,
                    access: "full",
                    tls: "skip"
                },
                {
                    host: "codeload.github.com",
                    port: 443,
                    access: "full",
                    tls: "skip"
                },
                {
                    host: "release-assets.githubusercontent.com",
                    port: 443,
                    access: "full",
                    tls: "skip"
                },
                {
                    host: "ghcr.io",
                    port: 443,
                    access: "full",
                    tls: "skip"
                },
                {
                    host: "pkg-containers.githubusercontent.com",
                    port: 443,
                    access: "full",
                    tls: "skip"
                },
                {
                    host: "objects.githubusercontent.com",
                    port: 443,
                    access: "full",
                    tls: "skip"
                },
                {
                    host: "raw.githubusercontent.com",
                    port: 443,
                    access: "full",
                    tls: "skip"
                }
            ],
            binaries: [
                {
                    path: "/usr/bin/curl"
                },
                {
                    path: "/usr/bin/git"
                },
                {
                    path: "/usr/local/bin/brew"
                },
                {
                    path: "/home/linuxbrew/.linuxbrew/bin/brew"
                },
                {
                    path: "/home/linuxbrew/.linuxbrew/bin/*"
                },
                {
                    path: "/home/linuxbrew/.linuxbrew/Homebrew/bin/*"
                },
                {
                    path: "/home/linuxbrew/.linuxbrew/Homebrew/Library/Homebrew/shims/shared/*"
                }
            ]
        }
    }
};
// ── Helpers ─────────────────────────────────────────────────────────────
function parsePolicy(val) {
    try {
        const parsed = JSON.parse(val);
        if (typeof parsed === "object" && parsed !== null)
            return parsed;
    }
    catch {
        /* ignore */
    }
    return null;
}
function policyToString(p) {
    return JSON.stringify(p, null, 2);
}
function clonePolicy(p) {
    return JSON.parse(JSON.stringify(p));
}
// ── Path List sub-component ────────────────────────────────────────────
const PathList = ({ paths, onChange, placeholder = "/path" }) => (React.createElement("div", { className: styles.pathList },
    paths.map((p, i) => (React.createElement("div", { key: i, className: styles.pathItem },
        React.createElement("input", { className: styles.inputSmall, value: p, onChange: (e) => {
                const next = [...paths];
                next[i] = e.target.value;
                onChange(next);
            }, placeholder: placeholder }),
        React.createElement("button", { className: styles.removeBtn, onClick: () => onChange(paths.filter((_, j) => j !== i)), title: "Remove" }, "\u00D7")))),
    React.createElement("button", { className: styles.addBtn, onClick: () => onChange([...paths, ""]) }, "+ Add path")));
// ── Collapsible section ────────────────────────────────────────────────
const Section = ({ title, badge, defaultOpen = false, info, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (React.createElement("div", { className: styles.section },
        React.createElement("button", { className: styles.sectionHeader, onClick: () => setOpen((v) => !v) },
            React.createElement("span", { className: styles.sectionTitle },
                React.createElement("span", { className: open ? styles.chevronOpen : styles.chevron }, "\u25B8"),
                title,
                info && React.createElement("span", { onClick: (e) => e.stopPropagation() }, info),
                badge === "static" && React.createElement("span", { className: styles.staticBadge }, "Static"),
                badge === "dynamic" && React.createElement("span", { className: styles.dynamicBadge }, "Hot-reload"))),
        open && React.createElement("div", { className: styles.sectionBody }, children)));
};
// ── Network policy entry editor ────────────────────────────────────────
const NetworkPolicyEntryEditor = ({ entryKey, entry, onChange, onRemove, onKeyChange }) => {
    const [open, setOpen] = useState(false);
    const updateEndpoint = (i, ep) => {
        const next = { ...entry, endpoints: entry.endpoints.map((e, j) => (j === i ? ep : e)) };
        onChange(next);
    };
    const removeEndpoint = (i) => {
        onChange({ ...entry, endpoints: entry.endpoints.filter((_, j) => j !== i) });
    };
    const addEndpoint = () => {
        onChange({
            ...entry,
            endpoints: [...entry.endpoints, { host: "", port: 443 }]
        });
    };
    return (React.createElement("div", { className: styles.policyEntry },
        React.createElement("button", { className: styles.policyEntryHeader, onClick: () => setOpen((v) => !v) },
            React.createElement("span", { className: styles.policyEntryName },
                React.createElement("span", { className: open ? styles.chevronOpen : styles.chevron }, "\u25B8"),
                entry.name || entryKey),
            React.createElement("span", { className: styles.policyEntryMeta },
                entry.endpoints.length,
                " endpoint",
                entry.endpoints.length !== 1 ? "s" : "",
                " · ",
                entry.binaries.length,
                " binar",
                entry.binaries.length !== 1 ? "ies" : "y")),
        open && (React.createElement("div", { className: styles.policyEntryBody },
            React.createElement("div", { className: styles.fieldRow },
                React.createElement("div", { className: styles.field },
                    React.createElement("div", { className: styles.labelWithInfo },
                        React.createElement("label", { className: styles.label }, "Key"),
                        React.createElement(InfoTip, { title: "Policy Key" }, "A unique identifier for this network policy block (e.g. \"github\" or \"npm_registry\"). Used internally to reference the policy. Use lowercase with underscores, no spaces.")),
                    React.createElement("input", { className: styles.inputSmall, value: entryKey, onChange: (e) => onKeyChange(entryKey, e.target.value) })),
                React.createElement("div", { className: styles.field },
                    React.createElement("div", { className: styles.labelWithInfo },
                        React.createElement("label", { className: styles.label }, "Display name"),
                        React.createElement(InfoTip, { title: "Display Name" }, "A friendly name that shows up in logs and monitoring dashboards. Helps you quickly identify which policy allowed or blocked a connection.")),
                    React.createElement("input", { className: styles.inputSmall, value: entry.name, onChange: (e) => onChange({ ...entry, name: e.target.value }) }))),
            React.createElement("div", { className: styles.labelWithInfo },
                React.createElement("span", { className: styles.subLabel }, "Endpoints"),
                React.createElement(InfoTip, { title: "Endpoints" }, "The external services (websites, APIs) this policy allows the agent to connect to. Each endpoint specifies a host and port. Only connections to these exact destinations will be permitted.")),
            entry.endpoints.map((ep, i) => (React.createElement(EndpointProfileEditor, { key: i, endpoint: ep, onChange: (ep) => updateEndpoint(i, ep), onRemove: () => removeEndpoint(i) }))),
            React.createElement("button", { className: styles.addBtn, onClick: addEndpoint }, "+ Add endpoint"),
            React.createElement("div", { className: styles.labelWithInfo },
                React.createElement("span", { className: styles.subLabel }, "Binaries"),
                React.createElement(InfoTip, { title: "Allowed Binaries" }, "The specific programs on the machine that are allowed to use these endpoints. For example, /usr/bin/git can access GitHub, or /usr/bin/node can access npm. This prevents other programs from piggybacking on allowed connections.")),
            React.createElement(PathList, { paths: entry.binaries.map((b) => b.path), onChange: (paths) => onChange({ ...entry, binaries: paths.map((p) => ({ path: p })) }), placeholder: "/usr/bin/node" }),
            React.createElement("button", { className: styles.deleteEntryBtn, onClick: onRemove }, "\u00D7 Remove policy")))));
};
export function ChannelsList({ openShellClient, slack, setSlack, discord, setDiscord, telegram, setTelegram }) {
    const [slackProviders, setSlackProviders] = useState([]);
    const [discordProviders, setDiscordProviders] = useState([]);
    const [telegramProviders, setTelegramProviders] = useState([]);
    const [newChannelType, setNewChannelType] = useState("slack");
    const [hasPendingChannel, setHasPendingChannel] = useState(false);
    const refreshProviders = () => {
        openShellClient.getProviders(["SLACK_BOT_TOKEN", "SLACK_APP_TOKEN"]).then(setSlackProviders);
        openShellClient.getProviders(["DISCORD_BOT_TOKEN"]).then(setDiscordProviders);
        openShellClient.getProviders(["TELEGRAM_BOT_TOKEN"]).then(setTelegramProviders);
    };
    useEffect(() => {
        refreshProviders();
    }, []);
    const handleToggle = (name) => {
        if (slackProviders.find((p) => p.name == name)) {
            if (slack === true) {
                setSlack(name === "slack" ? false : { enabled: true, provider: name });
            }
            else if (slack === undefined || slack === false) {
                setSlack({ enabled: true, provider: name });
            }
            else if (slack.provider === name) {
                setSlack(false);
            }
            else {
                setSlack({ enabled: true, provider: name });
            }
        }
        else if (discordProviders.find((p) => p.name == name)) {
            if (discord === true) {
                setDiscord(name === "discord" ? false : { enabled: true, provider: name, allowed_user_ids: [], guilds: [] });
            }
            else if (discord === undefined || discord === false) {
                setDiscord({ enabled: true, provider: name, allowed_user_ids: [], guilds: [] });
            }
            else if (discord.provider === name) {
                setDiscord(false);
            }
            else {
                setDiscord({ enabled: true, provider: name, allowed_user_ids: [], guilds: [] });
            }
        }
        else if (telegramProviders.find((p) => p.name == name)) {
            if (telegram === true) {
                setTelegram(name === "telegram" ? false : { enabled: true, provider: name, allowed_user_ids: [] });
            }
            else if (telegram === undefined || telegram === false) {
                setTelegram({ enabled: true, provider: name, allowed_user_ids: [] });
            }
            else if (telegram.provider === name) {
                setTelegram(false);
            }
            else {
                setTelegram({ enabled: true, provider: name, allowed_user_ids: [] });
            }
        }
    };
    const isChecked = (name) => {
        if (slackProviders.find((p) => p.name == name)) {
            return ((slack === true && name === "slack") ||
                (slack !== undefined &&
                    slack !== false &&
                    slack !== true &&
                    slack.enabled !== false &&
                    (slack.provider === name || (slack.provider === undefined && name === "slack"))));
        }
        else if (discordProviders.find((p) => p.name == name)) {
            return ((discord === true && name === "discord") ||
                (discord !== undefined &&
                    discord !== false &&
                    discord !== true &&
                    discord.enabled !== false &&
                    (discord.provider === name || (discord.provider === undefined && name === "discord"))));
        }
        else if (telegramProviders.find((p) => p.name == name)) {
            return ((telegram === true && name === "telegram") ||
                (telegram !== undefined &&
                    telegram !== false &&
                    telegram !== true &&
                    telegram.enabled !== false &&
                    (telegram.provider === name || (telegram.provider === undefined && name === "telegram"))));
        }
        return false;
    };
    return (React.createElement("div", { className: "flex flex-col gap-3" },
        React.createElement("ul", { className: "flex flex-col gap-2" },
            slackProviders.map(({ name }) => (React.createElement("li", { key: name, className: "flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5" },
                React.createElement("div", { className: "flex-1 min-w-0" },
                    React.createElement("p", { className: "text-sm font-medium font-mono" }, `${name} (Slack)`)),
                React.createElement(Toggle, { checked: isChecked(name), onChange: () => handleToggle(name), label: isChecked(name) ? `Enable ${name}` : `Disable ${name}` })))),
            discordProviders.map(({ name }) => (React.createElement("li", { key: name, className: "flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5" },
                React.createElement("div", { className: "flex-1 min-w-0" },
                    React.createElement("p", { className: "text-sm font-medium font-mono" }, `${name} (Discord)`)),
                React.createElement(Toggle, { checked: isChecked(name), onChange: () => handleToggle(name), label: isChecked(name) ? `Enable ${name}` : `Disable ${name}` })))),
            telegramProviders.map(({ name }) => (React.createElement("li", { key: name, className: "flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5" },
                React.createElement("div", { className: "flex-1 min-w-0" },
                    React.createElement("p", { className: "text-sm font-medium font-mono" }, `${name} (Discord)`)),
                React.createElement(Toggle, { checked: isChecked(name), onChange: () => handleToggle(name), label: isChecked(name) ? `Enable ${name}` : `Disable ${name}` }))))),
        !hasPendingChannel && (React.createElement("div", { className: styles.field },
            React.createElement("div", { className: styles.labelWithInfo },
                React.createElement("label", { className: styles.label }, "Channel Type"),
                React.createElement(InfoTip, { title: "Type" }, "The type of service this these credentials will be used to access. Defaults to \"generic\".")),
            React.createElement("select", { className: styles.select + " " + styles.inputSmall + (!hasPendingChannel ? "" : " " + styles.disabled), value: newChannelType, onChange: (e) => setNewChannelType(e.target.value), disabled: hasPendingChannel },
                React.createElement("option", { value: "slack" }, "Slack"),
                React.createElement("option", { value: "discord" }, "Discord"),
                React.createElement("option", { value: "telegram" }, "Telegram")))),
        React.createElement(ProviderCreator, { defaultProviderFactory: () => {
                switch (newChannelType) {
                    case "slack":
                        return {
                            name: "",
                            type: "generic",
                            credentials: [
                                { key: "SLACK_BOT_TOKEN", value: "" },
                                { key: "SLACK_APP_TOKEN", value: "" }
                            ],
                            configs: []
                        };
                    case "discord":
                        return {
                            name: "",
                            type: "generic",
                            credentials: [{ key: "DISCORD_BOT_TOKEN", value: "" }],
                            configs: []
                        };
                    case "telegram":
                        return {
                            name: "",
                            type: "generic",
                            credentials: [{ key: "TELEGRAM_BOT_TOKEN", value: "" }],
                            configs: []
                        };
                }
                return {
                    name: "",
                    type: "generic",
                    credentials: [],
                    configs: []
                };
            }, onAddClick: () => setHasPendingChannel(true), onClear: () => setHasPendingChannel(false), onCreated: () => {
                refreshProviders();
                setHasPendingChannel(false);
            }, openShellClient: openShellClient })));
}
export function ProvidersList({ openShellClient, providers, updateProviders }) {
    const [availableProviders, setAvailableProviders] = useState([]);
    const refreshProviders = () => {
        openShellClient.getProviders().then(setAvailableProviders);
    };
    useEffect(() => {
        refreshProviders();
    }, []);
    const providersSet = new Set(providers);
    const handleToggle = (name) => {
        if (providersSet.has(name)) {
            updateProviders(providers.filter((p) => p != name));
        }
        else {
            updateProviders(providers.concat([name]));
        }
    };
    const isChecked = (name) => {
        return providersSet.has(name);
    };
    const enabledCount = providers.length;
    return (React.createElement("div", { className: "flex flex-col gap-3" },
        React.createElement("div", { className: "flex items-center gap-2" },
            React.createElement("h3", { className: "text-base font-medium" }, "Credential Providers"),
            React.createElement("span", { className: "ml-auto text-xs text-gray-500" },
                enabledCount,
                " of ",
                availableProviders.length,
                " enabled")),
        React.createElement("p", { className: "text-sm text-gray-500 -mt-2" }, "Credential providers that your agent can use."),
        React.createElement("ul", { className: "flex flex-col gap-2" }, availableProviders.map(({ name }) => (React.createElement("li", { key: name, className: "flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5" },
            React.createElement("div", { className: "flex-1 min-w-0" },
                React.createElement("p", { className: "text-sm font-medium font-mono" }, name)),
            React.createElement(Toggle, { checked: isChecked(name), onChange: () => handleToggle(name), label: isChecked(name) ? `Enable ${name}` : `Disable ${name}` }))))),
        React.createElement(ProviderCreator, { defaultProviderFactory: () => ({
                name: "",
                type: "generic",
                credentials: [],
                configs: []
            }), onCreated: (created) => {
                setAvailableProviders((prev) => prev.some((p) => p.name === created.name) ? prev : prev.concat([{ name: created.name, type: created.type }]));
            }, openShellClient: openShellClient })));
}
// ── Main PolicyEditor ──────────────────────────────────────────────────
const PolicyEditor = ({ value, onChange, webSearch, onChangeWebSearch, openShellClient }) => {
    const [mode, setMode] = useState("visual");
    const [jsonText, setJsonText] = useState("");
    const [jsonError, setJsonError] = useState(null);
    // Parse the incoming value or use default
    const parsed = parsePolicy(value);
    const policy = parsed && parsed.version ? parsed : DEFAULT_POLICY;
    // Sync JSON text when switching to JSON mode
    useEffect(() => {
        if (mode === "json") {
            setJsonText(policyToString(policy));
            setJsonError(null);
        }
    }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
    // Emit changes
    const emitChange = useCallback((p) => onChange(policyToString(p)), [onChange]);
    const updatePolicy = useCallback((updater) => {
        const draft = clonePolicy(policy);
        updater(draft);
        emitChange(draft);
    }, [policy, emitChange]);
    // ── JSON mode handlers ──────────────────────────────────────────────
    const handleJsonChange = (text) => {
        setJsonText(text);
        try {
            const p = JSON.parse(text);
            setJsonError(null);
            onChange(JSON.stringify(p, null, 2));
        }
        catch (e) {
            setJsonError(e instanceof Error ? e.message : "Invalid JSON");
        }
    };
    // ── Network policies helpers ────────────────────────────────────────
    const netPolicies = policy.network_policies ?? {};
    const netEntries = Object.entries(netPolicies);
    const updateNetEntry = (key, entry) => {
        updatePolicy((d) => {
            if (!d.network_policies)
                d.network_policies = {};
            d.network_policies[key] = entry;
        });
    };
    const removeNetEntry = (key) => {
        updatePolicy((d) => {
            if (d.network_policies)
                delete d.network_policies[key];
        });
    };
    const renameNetEntry = (oldKey, newKey) => {
        if (!newKey || newKey === oldKey)
            return;
        updatePolicy((d) => {
            if (!d.network_policies)
                return;
            const entry = d.network_policies[oldKey];
            if (!entry)
                return;
            delete d.network_policies[oldKey];
            d.network_policies[newKey] = entry;
        });
    };
    const addNetEntry = () => {
        const key = `new_policy_${Date.now()}`;
        updatePolicy((d) => {
            if (!d.network_policies)
                d.network_policies = {};
            d.network_policies[key] = {
                name: key,
                endpoints: [{ host: "", port: 443 }],
                binaries: [{ path: "" }]
            };
        });
    };
    return (React.createElement("div", { className: styles.editor },
        React.createElement("div", { className: styles.modeToggleHolder },
            React.createElement("div", { style: { width: "75%" } },
                React.createElement("div", { style: { fontWeight: "bold" } }, "Advanced Agent Settings"),
                React.createElement("div", null, "Aible will automatically generate your agent with the following recommended settings.")),
            React.createElement("div", { className: styles.modeToggle },
                React.createElement("button", { className: mode === "visual" ? styles.modeBtnActive : styles.modeBtn, onClick: () => setMode("visual") }, "Visual"),
                React.createElement("button", { className: mode === "json" ? styles.modeBtnActive : styles.modeBtn, onClick: () => setMode("json") }, "JSON"))),
        mode === "json" ? (React.createElement("div", { className: styles.field },
            React.createElement("textarea", { className: styles.jsonEditor, value: jsonText, onChange: (e) => handleJsonChange(e.target.value), spellCheck: false }),
            jsonError && React.createElement("div", { className: styles.jsonError }, jsonError))) : (React.createElement(React.Fragment, null,
            React.createElement(ClawTaskItem, { type: "PERMISSION" },
                React.createElement(Section, { title: "Filesystem Policy", badge: "static", defaultOpen: true, info: React.createElement(InfoTip, { title: "Filesystem Policy" }, "Controls which folders the agent can access on this machine. Think of it like setting permissions on a shared drive \u2014 you decide what the agent can see and what it can modify. This is locked when the sandbox is created and cannot be changed while running.") },
                    React.createElement("label", { className: styles.checkbox },
                        React.createElement("input", { type: "checkbox", checked: policy.filesystem_policy?.include_workdir ?? false, onChange: (e) => updatePolicy((d) => {
                                if (!d.filesystem_policy)
                                    d.filesystem_policy = { include_workdir: false, read_only: [], read_write: [] };
                                d.filesystem_policy.include_workdir = e.target.checked;
                            }) }),
                        "Include working directory in read_write",
                        React.createElement(InfoTip, { title: "Include Working Directory" }, "When enabled, the agent's main working folder is automatically added to the read-write list. This is usually what you want \u2014 it lets the agent create and edit files in the project it's working on.")),
                    React.createElement("div", { className: styles.field },
                        React.createElement("div", { className: styles.labelWithInfo },
                            React.createElement("label", { className: styles.label }, "Read-only paths"),
                            React.createElement(InfoTip, { title: "Read-Only Paths" }, "Folders listed here can be viewed by the agent but not changed. Typically includes system directories like /usr, /lib, and /etc. This ensures the agent can reference system files without accidentally modifying them.")),
                        React.createElement(PathList, { paths: policy.filesystem_policy?.read_only ?? [], onChange: (paths) => updatePolicy((d) => {
                                if (!d.filesystem_policy)
                                    d.filesystem_policy = { include_workdir: false, read_only: [], read_write: [] };
                                d.filesystem_policy.read_only = paths;
                            }), placeholder: "/usr" })),
                    React.createElement("div", { className: styles.field },
                        React.createElement("div", { className: styles.labelWithInfo },
                            React.createElement("label", { className: styles.label }, "Read-write paths"),
                            React.createElement(InfoTip, { title: "Read-Write Paths" }, "Folders listed here can be both read and modified by the agent. Typically the project workspace (/sandbox) and temp files (/tmp). Keep this list minimal \u2014 only grant write access where the agent actually needs to save files.")),
                        React.createElement(PathList, { paths: policy.filesystem_policy?.read_write ?? [], onChange: (paths) => updatePolicy((d) => {
                                if (!d.filesystem_policy)
                                    d.filesystem_policy = { include_workdir: false, read_only: [], read_write: [] };
                                d.filesystem_policy.read_write = paths;
                            }), placeholder: "/sandbox" }))),
                React.createElement(Section, { title: "Landlock", badge: "static", info: React.createElement(InfoTip, { title: "Landlock Security" }, "Landlock is a Linux kernel security feature that enforces the filesystem restrictions above at the deepest level of the operating system. Even if the agent tries to bypass normal file permissions, Landlock will block it. This is your safety net.") },
                    React.createElement("div", { className: styles.field },
                        React.createElement("div", { className: styles.labelWithInfo },
                            React.createElement("label", { className: styles.label }, "Compatibility"),
                            React.createElement(InfoTip, { title: "Compatibility Mode" },
                                React.createElement("strong", null, "best_effort"),
                                " \u2014 Uses the best security available on this machine. Recommended for most cases. It will adapt to what the host supports.",
                                React.createElement("br", null),
                                React.createElement("br", null),
                                React.createElement("strong", null, "hard_requirement"),
                                " \u2014 Fails immediately if the machine doesn't support the required security level. Use this when you need a guarantee that full protection is in place.")),
                        React.createElement("select", { className: styles.select, value: policy.landlock?.compatibility ?? "best_effort", onChange: (e) => updatePolicy((d) => {
                                d.landlock = { compatibility: e.target.value };
                            }) },
                            React.createElement("option", { value: "best_effort" }, "best_effort"),
                            React.createElement("option", { value: "hard_requirement" }, "hard_requirement")))),
                React.createElement(Section, { title: "Process", badge: "static", info: React.createElement(InfoTip, { title: "Process Identity" }, "Controls which system user and group the agent runs as inside the sandbox. This is like choosing which \"account\" the agent logs in with. Running as a limited user (not root) prevents the agent from making system-level changes. The default \"sandbox\" user has restricted privileges.") },
                    React.createElement("div", { className: styles.fieldRow },
                        React.createElement("div", { className: styles.field },
                            React.createElement("div", { className: styles.labelWithInfo },
                                React.createElement("label", { className: styles.label }, "Run as user"),
                                React.createElement(InfoTip, { title: "Run As User" }, "The system username the agent operates under. Default is \"sandbox\" \u2014 a low-privilege user. Never set this to \"root\" as it would give the agent full system control.")),
                            React.createElement("input", { className: styles.inputSmall, value: policy.process?.run_as_user ?? "sandbox", onChange: (e) => updatePolicy((d) => {
                                    if (!d.process)
                                        d.process = { run_as_user: "sandbox", run_as_group: "sandbox" };
                                    d.process.run_as_user = e.target.value;
                                }) })),
                        React.createElement("div", { className: styles.field },
                            React.createElement("div", { className: styles.labelWithInfo },
                                React.createElement("label", { className: styles.label }, "Run as group"),
                                React.createElement(InfoTip, { title: "Run As Group" }, "The system group the agent belongs to. Works alongside the user to determine file access permissions. Default is \"sandbox\".")),
                            React.createElement("input", { className: styles.inputSmall, value: policy.process?.run_as_group ?? "sandbox", onChange: (e) => updatePolicy((d) => {
                                    if (!d.process)
                                        d.process = { run_as_user: "sandbox", run_as_group: "sandbox" };
                                    d.process.run_as_group = e.target.value;
                                }) }))))),
            React.createElement(ClawTaskItem, { type: "TOOLS_INTEGRATIONS" },
                React.createElement("div", { style: { marginTop: "20px", marginBottom: "20px" } },
                    React.createElement("div", null, "Enable Brave web search"),
                    React.createElement(ButtonGroup, null,
                        React.createElement(Button, { onClick: () => onChangeWebSearch({ ...(webSearch ?? {}), isEnabled: true }), active: webSearch?.isEnabled === true }, "Enable"),
                        React.createElement(Button, { onClick: () => onChangeWebSearch({ ...(webSearch ?? {}), isEnabled: false }), active: webSearch?.isEnabled === false }, "Disable")),
                    webSearch?.isEnabled === true && (React.createElement("div", null,
                        React.createElement("div", null, "Enter Brave API Key"),
                        React.createElement("input", { value: webSearch?.braveApiKey, onChange: (e) => onChangeWebSearch({ ...(webSearch ?? {}), braveApiKey: e?.currentTarget?.value }) })))),
                React.createElement(Section, { title: "Provider Profiles", badge: "static", defaultOpen: true, info: React.createElement(InfoTip, { title: "Provider Profiles", placement: "right" }, "Manage reusable provider profiles that define endpoints, binaries, and credential requirements for external services. Profiles are imported into the gateway and referenced by agents.") },
                    React.createElement(ProviderProfileEditor, { openShellClient: openShellClient })),
                React.createElement(Section, { title: "Credential Providers", badge: "static", defaultOpen: true, info: React.createElement(InfoTip, { title: "Credential Providers", placement: "right" }, "Manage the credentials AI agents can use to access external services. These credentials are managed by OpenShell and are never directly exposed to the AI agent. This information cannot be updated after a sandbox has been started.") },
                    React.createElement(ProviderEditor, { providers: [], onChange: (x) => { }, openShellClient: openShellClient })),
                React.createElement(Section, { title: "Network Policies", badge: "dynamic", defaultOpen: true, info: React.createElement(InfoTip, { title: "Network Policies", placement: "right" }, "Controls which external services the agent is allowed to contact over the network. Each policy block pairs a set of allowed destinations (like api.github.com) with specific programs that can reach them (like /usr/bin/git). Any connection not explicitly allowed here will be blocked. Unlike other settings, network policies can be updated on a running sandbox without restarting it.") },
                    netEntries.map(([key, entry]) => (React.createElement(NetworkPolicyEntryEditor, { key: key, entryKey: key, entry: entry, onChange: (e) => updateNetEntry(key, e), onRemove: () => removeNetEntry(key), onKeyChange: renameNetEntry }))),
                    React.createElement("button", { className: styles.addBtn, onClick: addNetEntry }, "+ Add network policy")))))));
};
export default PolicyEditor;
